import React, { useEffect, useMemo, useRef, useState } from "react";
import { hasPriceRange } from "@/lib/variants";
import { homeSets } from "@/lib/homesets";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ChevronLeft, ChevronRight, Star, ShieldCheck, ShoppingCart, Heart, Eye, Quote, Instagram, Pill } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useCart } from "@/context/CartContext";
import StoreShell from "@/components/StoreShell";
import { ManagedReviews, ManagedInstagram } from "@/components/storefront/HomeSections";
import { Reveal, useDayCountdown } from "@/components/pharmacy/hooks";
import { inr, rating, reviews, hexA } from "@/lib/storeutils";

const NAVY = "var(--m-navy, #1d2b6b)";
const pct = (p) => (p.compare_at_price ? Math.round((1 - p.price / p.compare_at_price) * 100) : 0);

export default function MediHome() {
  const s = useStore();
  const cart = useCart();
  const [params] = useSearchParams();
  const q = (params.get("q") || "").toLowerCase();
  const [activeCat, setActiveCat] = useState("All");
  useEffect(() => { if (q && !s.loading) document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" }); }, [q, s.loading]);

  if (s.loading || !s.store) return <div className="min-h-screen flex items-center justify-center"><span className="w-10 h-10 rounded-full border-4 border-slate-200 border-t-orange-500 animate-spin" /></div>;
  if (s.error) return <div className="min-h-screen flex items-center justify-center text-slate-500">{s.error}</div>;

  const { c } = s;
  const all = s.products;
  const featured = all.filter((p) => p.featured);
  const lead = featured.length ? featured : all;
  // three shelves with different products: latest, best deals (discounted), featured (see lib/homesets.js)
  const sets = homeSets(all, { fresh: 8, featured: 10, sale: 5 });
  const shown = all.filter((p) => p.name.toLowerCase().includes(q));
  const cats = s.categories;
  const catTiles = cats.map((name) => ({ name, image: all.find((p) => p.category === name)?.image, items: all.filter((p) => p.category === name) }));


  return (
    <StoreShell>
      <Hero s={s} slides={lead.slice(0, 3)} />
      <PromoCards s={s} items={[lead[0], lead[1] || lead[0], lead[2] || lead[0]]} />
      {catTiles.length > 0 && <Popular s={s} tiles={catTiles} />}
      {sets.fresh.length > 0 && <Carousel s={s} cart={cart} title="Latest products" items={sets.fresh} id="latest" />}
      <SecureStrip c={c} />
      {sets.sale.length > 0 && <BestDeals s={s} cart={cart} deals={sets.sale} />}
      <Bestsellers s={s} cart={cart} cats={cats} all={all} />
      <BannerPair s={s} a={lead[0]} b={lead[1] || lead[0]} coupon={s.coupons[0]} />

      <section id="shop" className="max-w-6xl mx-auto px-5 py-12">
        <Reveal><h2 className="text-center text-[28px] font-semibold" style={{ color: NAVY }}>{q ? `Results for “${q}”` : "Featured products"}</h2></Reveal>
        {!q && cats.length > 0 && <Reveal className="flex gap-2 flex-wrap justify-center mt-5">{["All", ...cats].map((x) => <button key={x} onClick={() => setActiveCat(x)} data-testid={`storefront-cat-${x}`} className="px-4 py-1.5 rounded-full text-[12px] font-semibold transition-all hover:-translate-y-0.5" style={activeCat === x ? { background: NAVY, color: "#fff" } : { background: "#f1f3fa", color: NAVY }}>{x}</button>)}</Reveal>}
        {(() => { const list = q ? shown : activeCat === "All" ? (sets.featured.length ? sets.featured : all) : all.filter((p) => p.category === activeCat);
          return list.length === 0 ? <div className="text-center py-16 text-slate-500">No products found.</div> : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-8" data-testid="storefront-products">
              {list.map((p, i) => <Reveal key={p.id} kind="rv-zoom" delay={(i % 5) * 80}><MediCard p={p} s={s} cart={cart} /></Reveal>)}
            </div>); })()}
      </section>

      <ManagedReviews s={s} layout="medi" fallback={<Reviews c={c} />} />
      <ManagedInstagram s={s} layout="medi" fallback={<Insta s={s} />} />
    </StoreShell>
  );
}

/* ---------- Hero ---------- */
function Hero({ s, slides }) {
  const { c, slug } = s;
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = Math.max(slides.length, 1);
  useEffect(() => { if (paused || n < 2) return undefined; const t = setInterval(() => setI((x) => (x + 1) % n), 5500); return () => clearInterval(t); }, [paused, n]);
  const p = slides[i];
  const off = p ? pct(p) : 0;
  return (
    <section className="relative overflow-hidden" style={{ background: "linear-gradient(120deg,#e6f1fb,#d6e8f8 60%,#cfe3f7)", minHeight: 400 }} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid="medi-hero">
      <span className="absolute -left-20 top-10 w-72 h-72 rounded-full rx-float-slow" style={{ background: "rgba(255,255,255,.5)" }} />
      <span className="absolute right-1/3 -bottom-24 w-80 h-80 rounded-full rx-float" style={{ background: "rgba(255,255,255,.35)" }} />
      <div className="relative max-w-6xl mx-auto px-5 md:px-16 py-14 grid md:grid-cols-2 gap-6 items-center">
        <div key={i}>
          <h1 className="rx-slide-up text-[40px] md:text-[56px] font-semibold leading-[1.05]" style={{ color: NAVY }}>{p ? p.name : `Welcome to ${s.store.name}`}</h1>
          <p className="rx-slide-up mt-3 text-[14px] text-slate-600" style={{ animationDelay: ".1s" }}>{s.store.tagline || "Only this week. Don't miss!"}</p>
          {p && <div className="rx-slide-up mt-5 flex items-baseline gap-3" style={{ animationDelay: ".2s" }}><span className="text-[44px] font-bold" style={{ color: c.accent }}>{inr(p.price)}</span>{p.compare_at_price ? <span className="text-xl line-through text-slate-400">{inr(p.compare_at_price)}</span> : null}</div>}
          <Link to={p ? `/store/${slug}/product/${p.id}` : "#shop"} data-testid="storefront-shop-collection" className="rx-slide-up rx-btn mt-6 inline-flex items-center gap-2 px-8 py-3 rounded-full text-[13px] font-bold" style={{ background: c.accent, color: "#fff", animationDelay: ".3s" }}>Shop now <ArrowRight size={15} /></Link>
        </div>
        <div className="relative h-[280px] md:h-[340px] flex items-center justify-center">
          <div key={`i${i}`} className="rx-slide-in relative w-64 h-64 md:w-80 md:h-80 rounded-full overflow-hidden bg-white rx-float" style={{ boxShadow: "0 30px 60px rgba(29,43,107,.25)", border: "8px solid #fff" }}>
            {p?.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover" /> : <Pill size={90} className="m-auto mt-24 text-slate-300" />}
          </div>
          {off > 0 && <span className="absolute top-2 right-6 md:right-16 w-20 h-20 rounded-full flex items-center justify-center text-white text-xl font-bold rx-beat" style={{ background: c.accent }}>-{off}%</span>}
        </div>
      </div>
      {n > 1 && <>
        <button onClick={() => setI((i - 1 + n) % n)} aria-label="Previous slide" className="absolute left-3 top-1/2 w-9 h-9 rounded-full bg-white/90 shadow flex items-center justify-center hover:scale-110 transition-transform"><ChevronLeft size={18} /></button>
        <button onClick={() => setI((i + 1) % n)} aria-label="Next slide" className="absolute right-3 top-1/2 w-9 h-9 rounded-full bg-white/90 shadow flex items-center justify-center hover:scale-110 transition-transform"><ChevronRight size={18} /></button>
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">{slides.map((_, k) => <button key={k} aria-label={`Slide ${k + 1}`} onClick={() => setI(k)} className="h-2 rounded-full transition-all duration-500" style={{ width: i === k ? 26 : 8, background: i === k ? NAVY : "rgba(29,43,107,.3)" }} />)}</div>
      </>}
    </section>
  );
}

/* ---------- Promo cards ---------- */
function PromoCards({ s, items }) {
  const { c, slug } = s;
  const tones = [["#ffd24d", "#1a1a2e"], ["#dbe9f7", NAVY], [NAVY, "#fff"]];
  return (
    <section className="max-w-6xl mx-auto px-5 py-8 grid md:grid-cols-3 gap-4">
      {items.map((p, k) => p && (
        <Reveal key={k} delay={k * 120} kind="rv-zoom">
          <Link to={`/store/${slug}/product/${p.id}`} className="rx-card rx-zoom relative flex items-center justify-between h-44 rounded-sm overflow-hidden px-6" style={{ background: tones[k][0], color: tones[k][1] }}>
            <div className="relative z-10 max-w-[55%]">
              <div className="text-[11px] font-semibold opacity-80">{p.category || "Featured"}</div>
              <h3 className="text-[17px] font-semibold leading-tight mt-1 line-clamp-2">{p.name}</h3>
              {pct(p) > 0 && <div className="text-xl font-bold mt-1" style={{ color: k === 1 ? c.accent : undefined }}>-{pct(p)}%</div>}
              <span className="rx-btn inline-block mt-3 text-[11px] font-bold px-4 py-1.5 rounded-full" style={{ background: c.accent, color: "#fff" }}>Shop now</span>
            </div>
            <div className="w-32 h-32 shrink-0 rounded-full overflow-hidden bg-white/80 rx-float" style={{ animationDelay: `${k * .7}s` }}>{p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover" />}</div>
          </Link>
        </Reveal>
      ))}
    </section>
  );
}

/* ---------- Popular categories ---------- */
function Popular({ s, tiles }) {
  const { c, slug } = s;
  return (
    <section id="categories" className="max-w-6xl mx-auto px-5 py-8">
      <Reveal><h2 className="text-center text-[18px] font-semibold mb-6" style={{ color: NAVY }}>Check out the most popular categories</h2></Reveal>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {tiles.map((t, k) => (
          <Reveal key={t.name} delay={k * 90} kind="rv-zoom">
            <div className="rx-card rounded-sm border bg-white overflow-hidden h-full" style={{ borderColor: "#eef0f6" }} data-testid={`storefront-cat-tile-${t.name}`}>
              <div className="rx-zoom relative h-32 flex items-end" style={{ background: "#f4f7fc" }}>
                {t.image && <img src={t.image} alt={t.name} className="absolute inset-0 w-full h-full object-cover opacity-90" />}
                <div className="relative p-3 w-full" style={{ background: "linear-gradient(0deg,rgba(255,255,255,.95),rgba(255,255,255,0))" }}><div className="text-[14px] font-semibold" style={{ color: NAVY }}>{t.name}</div><div className="text-[10px] text-slate-400">{t.items.length} products</div></div>
              </div>
              <ul className="p-3 space-y-1.5">
                {t.items.slice(0, 4).map((p) => <li key={p.id}><Link to={`/store/${slug}/product/${p.id}`} className="rx-link text-[12px] text-slate-500 hover:text-slate-900 line-clamp-1">{p.name}</Link></li>)}
                <li><a href="#shop" className="text-[11px] font-semibold inline-flex items-center gap-1 transition-all hover:gap-2" style={{ color: c.accent }}>See all <ArrowRight size={11} /></a></li>
              </ul>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------- Carousel ---------- */
function Carousel({ s, cart, title, items, id }) {
  const rail = useRef(null);
  const go = (d) => rail.current?.scrollBy({ left: d * 300, behavior: "smooth" });
  return (
    <section id={id} className="max-w-6xl mx-auto px-5 py-10">
      <Reveal><h2 className="text-center text-[28px] font-semibold mb-6" style={{ color: NAVY }}>{title}</h2></Reveal>
      <div className="relative">
        <button onClick={() => go(-1)} aria-label="Previous" className="absolute -left-3 top-1/2 z-10 w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center hover:scale-110 transition-transform"><ChevronLeft size={16} /></button>
        <button onClick={() => go(1)} aria-label="Next" className="absolute -right-3 top-1/2 z-10 w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center hover:scale-110 transition-transform"><ChevronRight size={16} /></button>
        <div ref={rail} className="flex gap-4 overflow-x-auto scroll-smooth pb-3" style={{ scrollSnapType: "x mandatory", scrollbarWidth: "none" }}>
          {items.map((p) => <div key={p.id} className="shrink-0 w-[210px]" style={{ scrollSnapAlign: "start" }}><MediCard p={p} s={s} cart={cart} /></div>)}
        </div>
      </div>
    </section>
  );
}

function SecureStrip({ c }) {
  return (
    <Reveal kind="rv-zoom" className="max-w-6xl mx-auto px-5">
      <div className="relative overflow-hidden rounded-sm flex items-center gap-3 px-6 py-4 border" style={{ background: "#effaf3", borderColor: "#bfe8cd" }}>
        <ShieldCheck size={20} className="rx-beat" style={{ color: "#16a34a" }} /><span className="text-[14px]"><b>100% Secure delivery</b> without contacting the courier</span>
        <a href="#shop" className="rx-btn ml-auto px-5 py-1.5 rounded-sm text-[12px] font-bold text-white" style={{ background: "#16a34a" }}>More</a>
      </div>
    </Reveal>
  );
}

/* ---------- Best deals ---------- */
function BestDeals({ s, cart, deals }) {
  const { c } = s;
  const t = useDayCountdown();
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <section id="deals" className="max-w-6xl mx-auto px-5 py-12">
      <Reveal className="flex items-center justify-center gap-3 mb-6 flex-wrap">
        <h2 className="text-[28px] font-semibold" style={{ color: NAVY }}>Best Deals of the week!</h2>
        <div className="flex items-center gap-1">{[t.h, t.m, t.s].map((n, k) => <React.Fragment key={k}><span className="w-8 h-9 rounded flex items-center justify-center text-[14px] font-bold text-white tabular-nums" style={{ background: c.accent }}>{pad(n)}</span>{k < 2 && <b style={{ color: c.accent }}>:</b>}</React.Fragment>)}</div>
      </Reveal>
      <Reveal className="rounded-sm border-2 p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" style={{ borderColor: c.accent }}>
        {deals.map((p) => (
          <div key={p.id}><MediCard p={p} s={s} cart={cart} bare /></div>
        ))}
      </Reveal>
    </section>
  );
}

/* ---------- Bestsellers with tabs ---------- */
function Bestsellers({ s, cart, cats, all }) {
  const [tab, setTab] = useState(cats[0] || "All");
  useEffect(() => { if (cats[0] && !cats.includes(tab)) setTab(cats[0]); }, [cats, tab]);
  const list = useMemo(() => (tab === "All" ? all : all.filter((p) => p.category === tab)).slice(0, 5), [all, tab]);
  const tabs = cats.length ? cats : ["All"];
  return (
    <section id="bestsellers" className="max-w-6xl mx-auto px-5 py-10">
      <Reveal className="text-center"><h2 className="text-[28px] font-semibold" style={{ color: NAVY }}>Top 5 Bestsellers in:</h2>
        <div className="flex gap-5 justify-center mt-3 text-[13px] flex-wrap">{tabs.map((t) => <button key={t} onClick={() => setTab(t)} className="relative pb-1 font-medium transition-colors" style={{ color: tab === t ? NAVY : "#6b7280" }}>{t}<span className="absolute left-0 -bottom-0 h-0.5 transition-all duration-300" style={{ width: tab === t ? "100%" : 0, background: NAVY }} /></button>)}</div>
      </Reveal>
      <div key={tab} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-8">
        {list.map((p, i) => <div key={p.id} className="rx-slide-up" style={{ animationDelay: `${i * 90}ms` }}><MediCard p={p} s={s} cart={cart} rank={i + 1} /></div>)}
      </div>
    </section>
  );
}

/* ---------- Banner pair ---------- */
function BannerPair({ s, a, b, coupon }) {
  const { c, slug } = s;
  return (
    <section className="max-w-6xl mx-auto px-5 py-6 grid md:grid-cols-2 gap-5">
      {[[a, "Get rid of bacteria in your home", "#dbe9f7"], [b, coupon ? `${coupon.discount_type === "percent" ? coupon.value + "%" : "₹" + coupon.value} OFF · ${coupon.code}` : b?.name, "#e3eefb"]].map(([p, t, bg], k) => p && (
        <Reveal key={k} kind={k ? "rv-right" : "rv-left"}><Link to={`/store/${slug}/product/${p.id}`} className="rx-zoom relative flex items-center h-48 rounded-sm overflow-hidden px-8" style={{ background: bg }}>
          {p.image && <img src={p.image} alt="" className="absolute right-0 top-0 h-full w-1/2 object-cover" style={{ WebkitMaskImage: "linear-gradient(90deg,transparent,#000 40%)", maskImage: "linear-gradient(90deg,transparent,#000 40%)" }} />}
          <div className="relative z-10 max-w-[55%]"><h3 className="text-[22px] font-semibold leading-tight" style={{ color: NAVY }}>{t}</h3><span className="rx-btn inline-block mt-4 text-[12px] font-bold px-5 py-2 rounded-full text-white" style={{ background: c.accent }}>Shop now</span></div>
        </Link></Reveal>
      ))}
    </section>
  );
}

/* ---------- Product card ---------- */
export function MediCard({ p, s, cart, rank, bare }) {
  const { c, slug } = s;
  const off = pct(p);
  const hasVariants = p.variants?.length > 0 || p.variant_items?.length > 0;
  const [added, setAdded] = useState(false);
  const add = (e) => {
    e.preventDefault();
    if (hasVariants) { window.location.href = `/store/${slug}/product/${p.id}`; return; }
    cart.add(p, null, 1); toast.success("Added to cart"); setAdded(true); setTimeout(() => setAdded(false), 1200);
  };
  return (
    <div data-testid={`storefront-product-${p.id}`} className={`rx-card rx-zoom group relative bg-white h-full flex flex-col ${bare ? "" : "border rounded-sm"}`} style={{ borderColor: "#eef0f6" }}>
      <Link to={`/store/${slug}/product/${p.id}`} className="relative block overflow-hidden h-40" style={{ background: "#f6f8fc" }}>
        {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover" /> : <Pill size={36} className="m-auto mt-14 text-slate-300" />}
        {off > 0 && <span className="absolute top-2 left-2 w-9 h-9 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ background: "#16a34a" }}>-{off}%</span>}
        {rank && <span className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold text-white" style={{ background: NAVY }}>{rank}</span>}
        <span className="absolute right-2 bottom-2 flex flex-col gap-1.5 translate-x-12 group-hover:translate-x-0 transition-transform duration-300">
          <span className="w-8 h-8 rounded-full bg-white shadow flex items-center justify-center hover:text-orange-500"><Heart size={14} /></span>
          <span className="w-8 h-8 rounded-full bg-white shadow flex items-center justify-center hover:text-orange-500"><Eye size={14} /></span>
        </span>
      </Link>
      <div className="p-3 flex flex-col flex-1">
        <Link to={`/store/${slug}/product/${p.id}`}><h3 className="text-[12px] font-medium leading-snug line-clamp-2 min-h-[32px] transition-colors group-hover:text-orange-500" style={{ color: NAVY }}>{p.name}</h3></Link>
        <div className="flex items-baseline gap-2 mt-1.5">{hasPriceRange(p) && <span className="text-[11px] text-slate-400">From</span>}<span className="text-[15px] font-bold" style={{ color: off ? c.accent : NAVY }}>{inr(p.price)}</span>{p.compare_at_price ? <span className="text-[11px] line-through text-slate-400">{inr(p.compare_at_price)}</span> : null}</div>
        <div className="flex items-center gap-0.5 mt-1">{[...Array(5)].map((_, i) => <Star key={i} size={10} fill={i < Math.round(rating(p.id)) ? "#f59e0b" : "none"} color="#f59e0b" />)}<span className="text-[10px] text-slate-400 ml-1">({reviews(p.id)})</span></div>
        <button onClick={add} disabled={p.stock <= 0} data-testid={`storefront-add-to-cart-btn-${p.id}`} className="rx-btn mt-3 w-full py-2 rounded-full text-[11px] font-bold flex items-center justify-center gap-1.5 disabled:opacity-50" style={{ background: added ? "#16a34a" : NAVY, color: "#fff" }}>
          <ShoppingCart size={12} /> {added ? "Added" : hasVariants ? "Select options" : p.stock <= 0 ? "Sold out" : "Add to cart"}
        </button>
      </div>
    </div>
  );
}

/* ---------- Reviews ---------- */
function Reviews({ c }) {
  const r = [["Albert Flores", "I ordered on Friday evening and my package was with me by Monday. Everything was genuine and well sealed."], ["Dianne Russell", "Prescription upload was easy and the pharmacist confirmed quickly. Great service."], ["Darlene Robertson", "Everything is perfect, I would recommend! Prices are fair too."], ["Brooklyn Simmons", "Fast delivery and genuine products. Will order again."]];
  return (
    <section id="reviews" className="py-14 overflow-hidden" style={{ background: "#fdf3e7" }}>
      <Reveal><h2 className="text-center text-[28px] font-semibold mb-8" style={{ color: NAVY }}><span style={{ color: c.accent }}>//</span> Latest reviews</h2></Reveal>
      <div className="rx-marquee gap-4">{[...r, ...r].map(([n, t], k) => <div key={k} className="w-[300px] shrink-0 mx-2 bg-white rounded-sm p-5 rx-card"><Quote size={18} style={{ color: c.accent }} /><p className="text-[12px] text-slate-600 mt-2 leading-relaxed">{t}</p><div className="flex gap-0.5 mt-3">{[...Array(5)].map((_, i) => <Star key={i} size={11} fill="#f59e0b" color="#f59e0b" />)}</div><div className="text-[12px] font-semibold mt-1" style={{ color: NAVY }}>{n}</div></div>)}</div>
    </section>
  );
}

function Insta({ s }) {
  const imgs = s.products.filter((p) => p.image).slice(0, 6);
  if (imgs.length === 0) return null;
  return (
    <section className="max-w-6xl mx-auto px-5 py-12">
      <Reveal><h2 className="text-center text-[18px] font-semibold mb-6" style={{ color: NAVY }}>Follow <b>@{s.store.name.replace(/\s+/g, "").toUpperCase()}</b> on Instagram</h2></Reveal>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {imgs.map((p, k) => <Reveal key={p.id} kind="rv-zoom" delay={k * 70}><Link to={`/store/${s.slug}/product/${p.id}`} className="rx-zoom group relative block aspect-square overflow-hidden"><img src={p.image} alt={p.name} className="w-full h-full object-cover" /><span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white" style={{ background: "color-mix(in srgb, var(--m-navy, #1d2b6b) 60%, transparent)" }}><Instagram size={22} /></span></Link></Reveal>)}
      </div>
    </section>
  );
}
